import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Descriptions, Empty, Modal, Space, Spin, Typography } from 'antd';
import moment from 'moment';
import * as lastChanceApi from '@apis/last-chance';
import LastChanceStatusTag from '@components/LastChanceStatusTag';
import { LastChanceSalesPurchase } from '@models/last-chance';
import { formatPhoneNumber } from '@utils/dataTransform';

const { Text } = Typography;

const formatDeadline = (value?: string) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : '-');

interface LastChanceSalesStatusModalProps {
  open: boolean;
  itemId?: string;
  onClose: () => void;
}

const PurchaseCard: React.FC<{ index: number; purchase: LastChanceSalesPurchase }> = ({ index, purchase }) => (
  <Card size="small" title={`구매 정보 ${index + 1}`}>
    <Descriptions column={2} size="small" bordered>
      <Descriptions.Item label="주문번호" span={2}>
        {purchase.orderUid ? <Text copyable={{ text: purchase.orderUid }}>{purchase.orderUid}</Text> : '-'}
      </Descriptions.Item>
      <Descriptions.Item label="구매자">{purchase.buyerName}</Descriptions.Item>
      <Descriptions.Item label="연락처">
        {purchase.buyerPhone ? formatPhoneNumber(purchase.buyerPhone) : '-'}
      </Descriptions.Item>
      <Descriptions.Item label="구매 수량" span={2}>
        {purchase.purchaseCount}
      </Descriptions.Item>
      <Descriptions.Item label="입력옵션" span={2}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{purchase.inputOptions || '-'}</span>
      </Descriptions.Item>
      <Descriptions.Item label="요청사항" span={2}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{purchase.requestMemo || '-'}</span>
      </Descriptions.Item>
    </Descriptions>
  </Card>
);

const LastChanceSalesStatusModal: React.FC<LastChanceSalesStatusModalProps> = ({ open, itemId, onClose }) => {
  const salesStatusQuery = useQuery({
    queryKey: ['admin-last-chance-sales-status', itemId],
    queryFn: () => lastChanceApi.getLastChanceSalesStatus(itemId!),
    enabled: open && !!itemId,
  });

  const data = salesStatusQuery.data?.data;

  return (
    <Modal
      title="판매현황"
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={
        <Button type="primary" onClick={onClose}>
          닫기
        </Button>
      }
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
      {salesStatusQuery.isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : salesStatusQuery.isError ? (
        <Empty description="판매 현황을 불러오지 못했습니다." />
      ) : data ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="상태">
              <LastChanceStatusTag item={{ status: data.status, deadlineAt: data.deadlineAt }} />
            </Descriptions.Item>
            <Descriptions.Item label="마감일">{formatDeadline(data.deadlineAt)}</Descriptions.Item>
            <Descriptions.Item label="재고">{data.stockQuantity}</Descriptions.Item>
            <Descriptions.Item label="판매">
              {data.purchasedCount}/{data.recruitmentCapacity}
            </Descriptions.Item>
          </Descriptions>

          {data.purchases.length > 0 ? (
            data.purchases.map((purchase, index) => (
              <PurchaseCard
                key={purchase.orderUid || `${purchase.buyerName}-${index}`}
                index={index}
                purchase={purchase}
              />
            ))
          ) : (
            <Empty description="구매 내역이 없습니다." />
          )}

          <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
            자세한 정보는 결제 내역에서 확인할 수 있습니다.
          </Text>
        </Space>
      ) : null}
    </Modal>
  );
};

export default LastChanceSalesStatusModal;
